import React, { useState } from "react";
import { useEvent } from "../../../context/EventContext";
import eventsAPI from "../../../api/eventsAPI";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MeetingForm from "./Meeting/MeetingForm";
import MeetingFormPreview from "./Meeting/MeetingFormPreview";

// * This component renders the meeting form and meeting preview based on the
//  * selectedEvent state from the EventContext. It also handles the submission of
//  * the meeting form data and updates the local state accordingly. It also
//  * handles the deletion and editing of meetings. It displays a modal when the
//  * "Add Connection" button is clicked and allows the user to send an invitation
//  * to another user. It also displays a list of users who have already been
//  * invited to the meeting and their connection status.
const Meeting = () => {
  const {
    meetingFormData,
    setMeetingFormData,
    selectedEvent,
    setLoading,
    setSelectedEvent,
    meetingAdded,
    setMeetingAdded,
    setSelectedUser,
    selectedUser,
    handleConnect,
    connStatus,
    fetchConnectionStatus,
  } = useEvent();

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [meetingEditIndex, setMeetingEditIndex] = useState(null); // To track which meeting is being edited
  const [inputValue, setInputValue] = useState(""); // To track user input for search
  const [modalVisible, setModalVisible] = useState(false);

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
      width: "460px",
      height: "320px",
      background: "#fff",
      color: "#000",
      border: "1px solid #ccc",
      borderRadius: "10px",
      padding: "20px",
      zIndex: "4",
      position: "fixed",
      //   ...(window.innerWidth <= 768 && {
      //     marginTop: "-50px",
      //   }),
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // This gives the screen a dark background
      zIndex: "3", // Ensures the overlay is behind the modal content
      height: "742vh",
    },
  };

  const { user } = useAuth();

  const LocationData = selectedEvent ? selectedEvent[1] : [];

  const form = selectedEvent[0] ? selectedEvent[0] : [];

  const mainEventID = form?._id;
  const startDate = form?.start_date;
  const endDate = form?.end_date;
  const startTime = form?.start_time;
  const endTime = form?.end_time;

  const meetingFormDetails = selectedEvent[9] ? selectedEvent[9] : [];

  const registeredUsers =
    selectedEvent && selectedEvent[6] ? selectedEvent[6] : [];

  // Mapping over registeredUsers and extracting the firstName from userID field
  const userOptions = registeredUsers.map((user) => ({
    value: user.userID._id, // Assuming userID contains the user object with _id
    label: user.userID.firstName, // Accessing the firstName from userID
    ...user,
  }));

  const locations = LocationData.filter(
    (location) => location.space_type === "meeting room"
  );

  //   * Handles the "Add Space" button click. Sets isFormVisible to true,
  //   * displaying the meeting form for creating a new meeting.
  const handleAddSpace = () => {
    setIsFormVisible(true);
  };

  //   * Handles the "Edit" button click by setting the meetingFormData state to
  //   * the meeting form details at the given index and setting the
  //   * isFormVisible state to true. This displays the meeting form with the
  //   * selected meeting's details pre-filled. It also sets the meetingEditIndex
  //   * state to the index of the meeting to be edited.
  const handleEdit = (index) => {
    setMeetingFormData(meetingFormDetails[index]);
    setIsFormVisible(true);
    setMeetingEditIndex(index);
  };

  //   * Handles the "Add Meeting" button click by setting the meetingFormData
  //   * state to an empty object with mainEventID, setting the isFormVisible state
  //   * to true, and setting the meetingEditIndex state to null. This displays
  //   * the meeting form for creating a new meeting.
  const handleSubAdd = () => {
    setMeetingFormData({
      agenda: "",
      locationID: "",
      start: null,
      end: null,
      invited: [],
      mainEventID: mainEventID,
    });
    setIsFormVisible(true);
    setMeetingEditIndex(null);
  };

  //   * Handles the submission of meeting form data by sending a POST request to the "/data/meeting" endpoint.
  //   * If successful, it adds the meeting room to the database, shows a success message, and resets the form data.
  //   * If the request fails, it logs an error message and resets the loading state.
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Ensure invited is an array
    const invitedList = Array.isArray(meetingFormData.invited)
      ? meetingFormData.invited
      : [];

    try {
      const response = await eventsAPI.post("/data/meeting", {
        ...meetingFormData,
        userID: user?.id,
        mainEventID,
        invites: invitedList,
      });

      if (response.status === 200) {
        toast.success("Meeting Room Added Successfully");
        // setMeetingAdded(true);
        setLoading(false);
        setIsFormVisible(false);
        setMeetingFormData({
          agenda: "",
          locationID: "",
          start: null,
          end: null,
          invited: [],
          mainEventID: "",
        });
        setMeetingAdded(true);
      }
    } catch (error) {
      console.log("Error Creating Meeting Room:", error);
      setLoading(false);
    }
  };

  // Handles the update of a meeting by sending a POST request to the /data/updateMeeting endpoint.
  // If successful, it updates the local state with the updated meeting data and shows a success message.
  // If the request fails, it shows an error message.
  // It also updates the local state by updating the selected event at index 9 with the updated data.
  const handleUpdate = async (e) => {
    e.preventDefault(); // Uncomment if using form submission
    setLoading(true);

    try {
      const response = await eventsAPI.post(
        "/data/updateMeeting",
        meetingFormData
      );

      if (response.status === 200) {
        toast.success("Meeting updated successfully!");
        setLoading(false);
        setIsFormVisible(false);

        // Update selectedEvent at the 9th index (index 9)
        if (selectedEvent && selectedEvent.length > 9) {
          const updatedSelectedEvent = selectedEvent.map(
            (event, index) =>
              index === 9 ? { ...event, ...response.data } : event // Update only the 9th index
          );

          setSelectedEvent(updatedSelectedEvent); // Update state with the new array
        }
      }
    } catch (error) {
      console.log("Error updating subEvents:", error);
      setLoading(false);
    }
  };

  //   * Handles the deletion of a meeting by sending a DELETE request to the /data/meeting/{id} endpoint.
  //   * If successful, it removes the meeting from the database, shows a success message, and resets the form data.
  //   * If the request fails, it shows an error message.
  //   * It also updates the local state by removing the deleted meeting from the selectedEvent at index 9.
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(`/data/meeting/${id}`);

      if (response.status === 200) {
        toast.success("Meeting related Invitation deleted successfully!");
        setLoading(false);
        setIsFormVisible(false);
        // Update selectedEvent at index 9 (or the index where the meeting is stored)
        if (selectedEvent && selectedEvent.length > 9) {
          // Remove the deleted meeting from the array by filtering it out based on `id`
          const updatedSelectedEvent = selectedEvent.filter(
            (event, index) => !(index === 9 && event._id === id) // Remove the item that matches the `id`
          );
          // Set the updated state without the deleted meeting
          setSelectedEvent(updatedSelectedEvent); // Update state with the new array
        }
      }
    } catch (error) {
      console.log("Error deleting subEvent:", error);
      setLoading(false);
    }
  };

  //   * Closes the connection modal by setting the state to false
  const handleClose = () => {
    setModalVisible(false);
  };

  //   * Handles the connection modal visibility and sets the state of the clicked user's data
  //   * and the connection status by calling the fetchConnectionStatus function.
  const handleConnection = (userData) => {
    setModalVisible(true);
    setSelectedUser(userData); // Set the clicked user's data
    fetchConnectionStatus(userData.userID._id); // Call to get status
  };

  return (
    <div className="meeting-room">
      {!isFormVisible && !meetingAdded && (
        <button onClick={handleAddSpace} className="add-space">
          Create Meeting
        </button>
      )}

      {isFormVisible ? (
        <MeetingForm
          locations={locations}
          startDate={startDate}
          endDate={endDate}
          startTime={startTime}
          endTime={endTime}
          userOptions={userOptions}
          inputValue={inputValue}
          setInputValue={setInputValue}
          meetingFormDetails={meetingFormDetails}
          handleConnection={handleConnection}
          meetingEditIndex={meetingEditIndex}
          handleSave={handleSave}
          handleUpdate={handleUpdate}
          handleDelete={handleDelete}
          modalVisible={modalVisible}
          customStyles={customStyles}
          handleClose={handleClose}
          selectedUser={selectedUser}
          connStatus={connStatus}
          handleConnect={handleConnect}
        />
      ) : (
        meetingAdded && (
          <MeetingFormPreview
            meetingFormDetails={meetingFormDetails}
            handleEdit={handleEdit}
            handleSubAdd={handleSubAdd}
          />
        )
      )}
    </div>
  );
};

export default Meeting;
